import React, { useEffect, useState } from "react";
import UserRegisterFrom from "./components/UserRegisterFrom";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axiosInstance";

const UserRegister = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<any>([]);
  const [id, setId] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmModal, setConfirmModal] = useState<{ id: any, block: boolean }|null>(null);
  
  // Check if user is Super Admin
  const role: any = localStorage.getItem("role");
  const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true" || Number(role) === 0;

  const handleOpen = (editId: any = null) => {
    setId(editId);
    setOpen(true);
  };
  const handleClose = () => {
    handleGetUser();
    setOpen(false);
    setId(null);
  };

  const handleGetUser = async () => {
    try {
      const resp: any = await axiosInstance.get(
        `/api/auth?searchQuery=${searchQuery}`
      );
      if (resp.status === 200) {
        setData(resp?.data?.data);
      }
    } catch (Error: any) {
      toast.error(Error?.response?.data?.body);
    }
  };

  const handleUpdateStatus = async (id: any, status: any) => {
    try {
      const resp: any = await axiosInstance.put(`/api/auth/${id}`, {
        block: status,
      });
      if (resp?.status === 200) {
        toast.success(status ? `${resp?.data?.data?.name} has been blocked.` : `${resp?.data?.data?.name} has been unblocked.`);
        handleGetUser();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.body);
    }
  };

  const handleDeleteUser = (userId: any) => {
    toast.info('Delete user ' + userId);
  };

  useEffect(() => {
    handleGetUser();
    // eslint-disable-next-line
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                User Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Organize and manage your users efficiently
              </p>
            </div>
            {/* Only Super Admin can create new users */}
            {isSuperAdmin && (
              <button
                onClick={() => handleOpen()}
                className="btn-primary flex items-center gap-2 px-6 py-3 shadow-lg"
              >
                <i className="fa-solid fa-plus"></i>
                <span>Create New User</span>
              </button>
            )}
          </div>

          {/* Filters Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    placeholder="Search users..."
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                    <i className="fa-solid fa-search"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {data?.map((item: any, index: any) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {item?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {item?.user_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {item?.role === 0 
                        ? "Super Admin" 
                        : item?.roleEntity?.name || `Role ${item?.role || "N/A"}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          !item?.block
                            ? "text-green-600 bg-green-50 dark:bg-green-900/30 dark:text-green-300"
                            : "text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-300"
                        }`}
                      >
                        {!item?.block ? "Active" : "Blocked"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center justify-start gap-2">
                      <button
                        onClick={() => handleOpen(item?.id)}
                        className="p-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <i className="fa-solid fa-edit"></i>
                      </button>
                      <button
                        onClick={() =>
                          setConfirmModal({ id: item.id, block: !item.block })
                        }
                        className={`p-2 rounded-lg transition-colors ${
                          item?.block
                            ? "text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
                            : "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        }`}
                        title={item?.block ? "Unblock" : "Block"}
                      >
                        {item?.block ? (
                          <i className="fa-solid fa-check-circle"></i>
                        ) : (
                          <i className="fa-solid fa-ban"></i>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!data || data.length === 0) && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-8 text-center text-gray-400 dark:text-gray-500"
                    >
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Custom Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={handleClose}
        >
          <div
            className="relative overflow-y-auto max-h-[90vh] w-full max-w-lg mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <UserRegisterFrom onClose={handleClose} id={id} />
          </div>
        </div>
      )}
      {/* Block/Unblock Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setConfirmModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4 text-center">
              {confirmModal.block ? 'Are you sure you want to block this user?' : 'Are you sure you want to unblock this user?'}
            </h2>
            <div className="flex justify-center gap-4 mt-6">
              <button
                className="btn-primary"
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  handleUpdateStatus(confirmModal.id, confirmModal.block);
                  setConfirmModal(null);
                }}
              >
                {confirmModal.block ? 'Block' : 'Unblock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRegister;
